import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { StartOwnInsightsResponse } from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useFetchJobStatus } from '@upright/ui/api/internal/jobs/use-fetch-job-status';
import { useEffect } from 'react';

// The same pause as `useFetchJobStatus`'s own polling interval.
const STATUS_RETRY_DELAY_MS = 3000;

/** Where the tailored version is once own insights were started: `undefined` until the route has answered. */
export type OwnInsightsVersionState = 'computing' | 'ready' | 'failed' | undefined;

/**
 * PLT-7869 — turns own insights on for a company and follows the tailored version's computation, for the company
 * page's "Add own insights" button and the chat's offer card.
 *
 * Only a successful computation makes the version ready: a failed one leaves a version without results, which the
 * page must not switch to. Once it is ready, the page's tailoring state is refreshed. The grant changes what the
 * access details say, and those are cached apart from the variants (`refetchOnMount: false`, no variant in the
 * key), so without the refresh a user whose only tailoring rights are this grant sees neither the Model dropdown
 * nor the edit actions until a reload. Team users pass the privilege check regardless, so they never notice.
 */
export function useStartOwnInsights(companyId: string) {
  const queryClient = useQueryClient();
  const startMutation = useMutation({
    mutationFn: async () =>
      await fetchOrThrow<StartOwnInsightsResponse>(`/materiality/${companyId}/own-insights`, 'POST'),
  });
  const started = startMutation.data;
  const computeJob = useFetchJobStatus(started?.computeJobId);
  const versionState = getVersionState(started, computeJob.data?.status);

  // `useFetchJobStatus` stops polling when a status request fails. A failed request says nothing about the
  // computation, so polling resumes rather than reporting a failure the job never had, after the hook's own
  // polling interval so that a server returning errors is not asked again back to back.
  useEffect(() => {
    if (!computeJob.isError || versionState !== 'computing') return;
    const timeout = setTimeout(() => {
      computeJob.resetPolling();
    }, STATUS_RETRY_DELAY_MS);
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeJob.errorUpdatedAt, versionState]);

  useEffect(() => {
    if (versionState !== 'ready') return;
    void queryClient.invalidateQueries({ queryKey: ['get-access-details'] });
    void queryClient.invalidateQueries({ queryKey: ['materiality'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState]);

  return { startMutation, started, versionState };
}

function getVersionState(
  started: StartOwnInsightsResponse | undefined,
  computeStatus: JobStatus | undefined,
): OwnInsightsVersionState {
  if (started === undefined) return undefined;
  // A company that already had a tailored version starts nothing.
  if (started.computeJobId === undefined || computeStatus === JobStatus.SUCCESS) return 'ready';
  if (computeStatus === JobStatus.ERROR) return 'failed';
  return 'computing';
}
